import { type Money, money, allocate } from "../money/money";
import type { IsoTimestamp } from "../time";

/**
 * Voids and refunds (T156, FR-021, FR-022, FR-023, FR-025).
 *
 * A CORRECTION IS A NEW RECORD, NEVER AN EDIT. Nothing in this file takes a
 * sale and hands back a modified one; every function returns a separate record
 * that REFERENCES the sale. That is not a stylistic preference — it is the
 * difference between a journal and a spreadsheet. Once an amount has been
 * printed on a customer's receipt, the only honest way to change the books is
 * to write down what happened next.
 *
 * VOID VERSUS REFUND. A void reverses a sale entirely: it should not have been
 * rung. A refund returns part or all of what was paid on a sale that did
 * happen. They are not interchangeable, and the difference shows up at the
 * drawer — a voided cash sale and a cash refund both take money out, but only
 * the refund leaves the original sale standing in the day's gross.
 *
 * The document number is never reused, never returned to the pool, and never
 * carried by the correction itself (FR-023). A voided sale keeps its number
 * forever; that number's story is simply "issued, then voided".
 */

export class CorrectionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CorrectionError";
  }
}

// ---------------------------------------------------------------------------
// Void
// ---------------------------------------------------------------------------

export interface VoidInput {
  voidId: string;
  saleId: string;
  saleTotal: Money;
  reason: string;
  staffId: string;
  /** The manager who approved it. Both identities are recorded (FR-056). */
  authorizedBy: string;
  alreadyVoided: boolean;
  /** Cumulative refunds already issued against this sale. */
  refundedSoFar: Money;
  now: Date;
}

export interface VoidRecord {
  id: string;
  saleId: string;
  reason: string;
  staffId: string;
  authorizedBy: string;
  createdAt: IsoTimestamp;
}

export function buildVoid(input: VoidInput): VoidRecord {
  requireReason(input.reason, "void");
  requireAuthorization(input.authorizedBy, "void");

  // The server's UNIQUE(sale_id) on `void` makes this structurally impossible
  // too. Catching it here means the cashier is told why, at the terminal,
  // during an outage — rather than finding out when the envelope syncs.
  if (input.alreadyVoided) {
    throw new CorrectionError("This sale has already been voided.");
  }

  // A void reverses the whole sale. Money already returned as a refund would
  // otherwise be returned a second time.
  if (input.refundedSoFar > 0) {
    throw new CorrectionError(
      "This sale has already been partly refunded and cannot be voided. " +
        "Refund the remaining lines instead.",
    );
  }

  return {
    id: input.voidId,
    saleId: input.saleId,
    reason: input.reason.trim(),
    staffId: input.staffId,
    authorizedBy: input.authorizedBy,
    createdAt: input.now.toISOString() as IsoTimestamp,
  };
}

// ---------------------------------------------------------------------------
// Refund
// ---------------------------------------------------------------------------

export interface RefundableLine {
  saleLineId: string;
  /** Units sold on this line. */
  quantity: number;
  /** What the line actually charged, after its share of any discount. */
  lineTotal: Money;
  /** Units already returned by earlier refunds. */
  refundedQuantity: number;
}

/**
 * What a line is still worth, unit by unit.
 *
 * A line of 3 at ₱100.00 is not three ₱33.33 units — that loses a centavo, and
 * three separate partial refunds would return ₱99.99 of a ₱100.00 line. The
 * largest-remainder split gives [3334, 3333, 3333], so however the units are
 * refunded, in whatever order and across however many separate refunds, they
 * add back up to exactly what was charged.
 */
export function refundableUnits(line: RefundableLine): number[] {
  const units = allocate(line.lineTotal, new Array(line.quantity).fill(1));
  return units.slice(line.refundedQuantity);
}

export interface RefundLineSelection {
  line: RefundableLine;
  /** How many units of this line to return. */
  quantity: number;
}

export interface RefundInput {
  refundId: string;
  saleId: string;
  saleTotal: Money;
  reason: string;
  staffId: string;
  authorizedBy: string;
  /** Cash out of the drawer, as opposed to a reversal on the original tender. */
  isCash: boolean;
  /** Cumulative refunds already issued against this sale. */
  refundedSoFar: Money;
  alreadyVoided: boolean;
  lines: RefundLineSelection[];
  now: Date;
}

export interface RefundRecordLine {
  saleLineId: string;
  quantity: number;
  amount: Money;
}

export interface RefundRecord {
  id: string;
  saleId: string;
  reason: string;
  staffId: string;
  authorizedBy: string;
  totalAmount: Money;
  isCash: boolean;
  lines: RefundRecordLine[];
  createdAt: IsoTimestamp;
}

export function buildRefund(input: RefundInput): RefundRecord {
  requireReason(input.reason, "refund");
  requireAuthorization(input.authorizedBy, "refund");

  if (input.alreadyVoided) {
    throw new CorrectionError(
      "This sale was voided in full. There is nothing left to refund.",
    );
  }

  const selected = input.lines.filter((selection) => selection.quantity > 0);
  if (selected.length === 0) {
    throw new CorrectionError("Select at least one line to refund.");
  }

  const lines: RefundRecordLine[] = selected.map((selection) => {
    const available = refundableUnits(selection.line);

    if (selection.quantity > available.length) {
      throw new CorrectionError(
        `Line ${selection.line.saleLineId} has only ${available.length} unit(s) remaining ` +
          `to refund, not ${selection.quantity}.`,
      );
    }

    // Take from the front. Combined with the fixed largest-remainder split
    // above, this makes the sequence of partial refunds deterministic: the same
    // units always carry the same amounts, whoever refunds them and whenever.
    const amount = available
      .slice(0, selection.quantity)
      .reduce((total, unit) => total + unit, 0);

    return {
      saleLineId: selection.line.saleLineId,
      quantity: selection.quantity,
      amount: money(amount),
    };
  });

  const totalAmount = lines.reduce((total, line) => total + line.amount, 0);

  // FR-025 — CUMULATIVE, not per-refund. The server re-checks this against its
  // own record of prior refunds when the envelope arrives, because a terminal
  // that has been offline may not know about a refund taken on another one.
  const cumulative = input.refundedSoFar + totalAmount;
  if (cumulative > input.saleTotal) {
    throw new CorrectionError(
      `Refunding ${totalAmount} would bring the total refunded to ${cumulative}, ` +
        `which would exceed the sale total of ${input.saleTotal}.`,
    );
  }

  return {
    id: input.refundId,
    saleId: input.saleId,
    reason: input.reason.trim(),
    staffId: input.staffId,
    authorizedBy: input.authorizedBy,
    totalAmount: money(totalAmount),
    isCash: input.isCash,
    lines,
    createdAt: input.now.toISOString() as IsoTimestamp,
  };
}

/** Cumulative refunds against one sale. */
export function refundedTotal(refunds: ReadonlyArray<{ totalAmount: Money }>): Money {
  return money(refunds.reduce((total, refund) => total + refund.totalAmount, 0));
}

/**
 * The status a sale displays.
 *
 * DERIVED, never stored back onto the sale. The sale row is append-only, so its
 * status is a function of which correction records reference it — exactly as
 * the server treats it. Computing it in one place keeps the terminal and the
 * books from ever disagreeing about what a document is.
 */
export function deriveStatus(
  saleTotal: Money,
  isVoided: boolean,
  refundedSoFar: Money,
): "finalized" | "voided" | "refunded" | "partially_refunded" {
  if (isVoided) return "voided";
  if (refundedSoFar <= 0) return "finalized";
  return refundedSoFar >= saleTotal ? "refunded" : "partially_refunded";
}

// ---------------------------------------------------------------------------

function requireReason(reason: string, kind: string): void {
  if (!reason || reason.trim().length === 0) {
    throw new CorrectionError(`A ${kind} needs a reason.`);
  }
}

function requireAuthorization(authorizedBy: string, kind: string): void {
  if (!authorizedBy || authorizedBy.trim().length === 0) {
    throw new CorrectionError(`A ${kind} needs to be authorised by a manager.`);
  }
}
